import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Check, Copy, Download, Eye, EyeOff } from "lucide-react";
import { TwoFactorAuthService, TwoFactorAuthData, TwoFactorAuthStorage } from "../../lib/twoFactorAuth";

export const TwoFactorSetup = (): JSX.Element => {
  const navigate = useNavigate();
  const [twoFactorData, setTwoFactorData] = useState<TwoFactorAuthData | null>(null);
  const [verificationToken, setVerificationToken] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initializeTwoFactor();
  }, []);

  const initializeTwoFactor = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Initializing 2FA setup...');
      
      const data = await TwoFactorAuthService.generateTwoFactorAuth();
      console.log('2FA data generated successfully:', data ? 'yes' : 'no');
      setTwoFactorData(data);
    } catch (err) {
      console.error('Error initializing 2FA:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate 2FA setup. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyToken = async () => {
    if (!twoFactorData) return;

    try {
      setError(null);
      const isValid = await TwoFactorAuthService.verifyToken(verificationToken, twoFactorData.secret);
      
      if (isValid) {
        setIsVerified(true);
        // Store the secret and backup codes
        TwoFactorAuthStorage.setSecret(twoFactorData.secret);
        TwoFactorAuthStorage.setBackupCodes(twoFactorData.backupCodes);
      } else {
        setError("Invalid verification code. Please try again.");
      }
    } catch (err) {
      console.error('Error verifying token:', err);
      setError("Error verifying code. Please try again.");
    }
  };

  const handleCompleteSetup = () => {
    navigate('/aAyuiOcvMq/dashboard');
  };

  const copyBackupCodes = async () => {
    if (!twoFactorData) return;
    
    try {
      await navigator.clipboard.writeText(TwoFactorAuthService.formatBackupCodes(twoFactorData.backupCodes));
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2000);
    } catch (err) {
      setError("Failed to copy backup codes");
    }
  };

  const downloadBackupCodes = () => {
    if (!twoFactorData) return;
    
    const content = `RAWPA Admin - Backup Codes\n\n${TwoFactorAuthService.formatBackupCodes(twoFactorData.backupCodes)}\n\nStore these codes securely. Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rawpa-admin-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-black">Setting up 2FA...</p>
        </div>
      </div>
    );
  }

  if (!twoFactorData) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={initializeTwoFactor}>Retry Setup</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 min-h-screen">
        <div className="w-full max-w-[600px] bg-white rounded-[20px] p-8 shadow-lg mt-[140px]">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-[#daed6e] rounded-full flex items-center justify-center mb-4">
              <img src="/security-icon.svg" alt="2FA" className="w-8 h-8" />
            </div>
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Two-Factor Authentication Setup
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80 text-center">
              Secure your admin account with Google Authenticator
            </p>
          </div>

          {!isVerified ? (
            <div className="space-y-6">
              {/* Step 1: QR Code */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Step 1: Scan QR Code</CardTitle>
                  <CardDescription>
                    Open Google Authenticator and scan this QR code to add your admin account
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center space-y-4">
                  <div className="border-2 border-gray-200 rounded-lg p-4">
                    <img 
                      src={twoFactorData.qrCodeUrl} 
                      alt="QR Code for Google Authenticator" 
                      className="w-48 h-48"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Or manually enter this secret:</p>
                    <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono break-all">
                      {twoFactorData.secret}
                    </code>
                  </div>
                </CardContent>
              </Card>

              {/* Step 2: Verification */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Step 2: Verify Setup</CardTitle>
                  <CardDescription>
                    Enter the 6-digit code from Google Authenticator to verify your setup
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {error && (
                    <div className="p-3 text-sm text-red-500 bg-red-50 rounded-lg">
                      {error}
                    </div>
                  )}
                  <div className="flex space-x-2">
                    <Input
                      type="text"
                      value={verificationToken}
                      onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="text-center text-lg font-mono tracking-widest"
                      maxLength={6}
                    />
                    <Button 
                      onClick={handleVerifyToken}
                      disabled={verificationToken.length !== 6}
                      className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    >
                      Verify
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Success Message */}
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-2 text-green-700">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Two-factor authentication setup complete!</span>
                  </div>
                </CardContent>
              </Card>

              {/* Backup Codes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Backup Codes</CardTitle>
                  <CardDescription>
                    Save these backup codes in a secure location. You can use them to access your account if you lose your authenticator device.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">
                      {twoFactorData.backupCodes.length} backup codes
                    </Badge>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowBackupCodes(!showBackupCodes)}
                      >
                        {showBackupCodes ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        {showBackupCodes ? 'Hide' : 'Show'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyBackupCodes}
                      >
                        {copiedCodes ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copiedCodes ? 'Copied!' : 'Copy'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadBackupCodes}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </Button>
                    </div>
                  </div>
                  
                  {showBackupCodes && (
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                        {twoFactorData.backupCodes.map((code, index) => (
                          <div key={index} className="bg-white px-3 py-2 rounded border">
                            {code}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Complete Setup */}
              <Button 
                onClick={handleCompleteSetup}
                className="w-full h-12 bg-[#daed6e] hover:bg-[#c5d85c] text-black"
              >
                <span className="font-['Space_Grotesk'] font-medium text-base">
                  Complete Setup & Access Admin Panel
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 